import { useMemo } from 'react';
import { useQuery } from './client';

// ----------------------------------------------------------------------

export interface PlangroupFilterOption {
  readonly id: number;
  readonly special_name_en: string | null;
  readonly special_name_ar: string | null;
  readonly restaurant: {
    readonly id: number;
    readonly name_en: string;
    readonly name_ar: string;
  };
  readonly plan_tag: {
    readonly id: number;
    readonly name_en: string;
    readonly name_ar: string;
  };
  readonly protein_size: number | null;
  readonly carb_size: number | null;
}

export function useGetPlangroupFilterOptions(restaurantId?: number | string) {
  // Build query URL with optional restaurant filter
  const queryUrl = useMemo(() => {
    const baseUrl = '/partners/filter-options/plangroups';
    if (restaurantId) {
      return `${baseUrl}?restaurant=${restaurantId}`;
    }
    return baseUrl;
  }, [restaurantId]);

  const { data, isLoading, error, isValidating } = useQuery(queryUrl);

  const memoizedValue = useMemo(
    () => {
      // Extract unique plan tags from the plangroup filter options
      const uniquePlanTags = data ? 
        Array.from(
          new Map(
            data.map((item: PlangroupFilterOption) => [
              item.plan_tag.id, 
              item.plan_tag
            ])
          ).values()
        ) : [];

      return {
        plangroupFilterOptions: data || [],
        planTags: uniquePlanTags,
        planGroups: data || [], // Return plangroups for macro details
        plangroupFilterOptionsLoading: isLoading,
        plangroupFilterOptionsError: error,
        plangroupFilterOptionsValidating: isValidating,
        plangroupFilterOptionsEmpty: !isLoading && !data?.length,
      };
    },
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export interface RestaurantFilterOption {
  readonly id: number;
  readonly name_en: string;
  readonly name_ar: string;
}

export function useGetRestaurantFilterOptions() {
  const { data, isLoading, error, isValidating } = useQuery('/partners/filter-options/restaurants');

  const memoizedValue = useMemo(
    () => ({
      restaurantFilterOptions: data || [],
      restaurantFilterOptionsLoading: isLoading,
      restaurantFilterOptionsError: error,
      restaurantFilterOptionsValidating: isValidating,
      restaurantFilterOptionsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}